<script setup>
// Global filters are attached to the entire dashboard. However, if we want some filters that can be applied quickly within the asset scope, we can do that here.
import { cloneDeep, isEqual } from 'lodash-es';
import { storeToRefs } from 'pinia';
import { computed, nextTick, onBeforeMount, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import BiQueryBuilderColumnPicker from '~/bi/components/query-builder/column-picker/bi-query-builder-column-picker.vue';
import { useBIQueryBuilder } from '~/bi/composables/useBIQueryBuilder.js';
import { useBiStore } from '~/bi/store/bi.store';
import { useCommonImports } from '~/common/composables/common-imports.composable';
import { getTextWidth } from '~/common/utils/common.utils.js';

const props = defineProps({
  widgetId: {
    type: String,
    required: true,
  },
  // NOTE: We are getting originalConfig from here in the grid mode (without global filters)
  config: {
    type: Object,
    required: true,
  },
  widgetData: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['applyFilters']);

const { $t, $services } = useCommonImports();
const bi_store = useBiStore();
const { getColumnsForSeries, getStageConfig } = useBIQueryBuilder();
const { all_schema_by_name, widget_builder_config, selected_dashboard_details, get_applied_filters_for_dashboard } = storeToRefs(bi_store);

const state = reactive({
  is_settings_open: false,
  is_loading_values: false,
  active_filters: {}, // { field: selected_value }
  search_queries: {}, // { field: search_query }
  distinct_values_map: {}, // { field: distinct_values[] }
  max_tab_values: 5,
});

const filters_container_ref = ref(null);
let resize_observer = null;

const DATE_FILTER_OPTIONS = [
  'today',
  'yesterday',
  'this_week',
  'this_month',
  'this_quarter',
  'this_year',
  'last_week',
  'last_month',
  'last_quarter',
  'last_year',
  'next_week',
  'next_month',
  'next_quarter',
  'next_year',
];

const DATE_INTERVAL_OPTIONS = [
  { label: $t('Day'), value: 'day' },
  { label: $t('Week'), value: 'week' },
  { label: $t('Month'), value: 'month' },
  { label: $t('Quarter'), value: 'quarter' },
  { label: $t('Year'), value: 'year' },
];

const pending_date_column = ref(null);

function isDateField(field_type) {
  return ['date', 'timestamp', 'datetime', 'timestamptz'].includes(field_type);
}

function formatDateFilterValue(value) {
  return value
    .split('_')
    .map((word, index) => index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word)
    .join(' ');
}

function formatDateIntervalValue(value) {
  const option = DATE_INTERVAL_OPTIONS.find(opt => opt.value === value);
  return option ? option.label : value;
}

function isDateIntervalFilter(filter_field) {
  return filter_field.date_filter_mode === 'date_interval';
}

function getFilterKey(filter_field) {
  // Create a unique key for each filter based on field and date_filter_mode
  if (filter_field.date_filter_mode) {
    return `${filter_field.field}__${filter_field.date_filter_mode}`;
  }
  return filter_field.field;
}

const displayed_quick_filters = computed(() => {
  if (props.widgetId === 'preview') {
    return widget_builder_config.value.quick_filters;
  }
  return props.config.quick_filters || [];
});

function getTablesForStage(stage, stage_index) {
  const tables = [];

  if ((stage.table || stage_index === 0) && (all_schema_by_name.value[stage.table] || stage.table?.generateSeries)) {
    const table_label = stage.table?.alias || all_schema_by_name.value[stage.table]?.label;
    tables.push({
      label: table_label,
      name: stage.table,
      columns: stage.table?.generateSeries ? getColumnsForSeries(stage.table) : all_schema_by_name.value[stage.table].columns,
    });
  }

  stage.joins?.forEach((join) => {
    if (all_schema_by_name.value?.[join?.table?.name]) {
      const table_label = all_schema_by_name.value[join.table.name].label;
      tables.push({
        label: table_label,
        name: join.table.name,
        columns: all_schema_by_name.value[join.table.name].columns,
      });
    }
  });

  return tables;
}

const stages = computed(() => {
  if (!props.config?.query?.stages?.length)
    return [];

  return props.config.query.stages.map((stage, index) => ({
    label: `Stage ${index + 1}`,
    columns: stage.columns?.filter(field => !field.skip && ['date', 'timestamp', 'datetime', 'timestamptz'].includes(field.field_type)).map(field => ({
      ...field,
      type: field.field_type,
      label: field.alias,
    })) || [],
    tables: getTablesForStage(stage, index),
  }));
});

async function onColumnSelected(column_config) {
  // For non-date fields, check if already added
  if (!isDateField(column_config.field_type)) {
    if (widget_builder_config.value.quick_filters?.find?.(f => f.field === column_config.field)) {
      state.is_settings_open = false;
      return;
    }
    await addQuickFilter(column_config);
    return;
  }

  // For date fields, show the date filter mode selection
  pending_date_column.value = column_config;
}

async function onDateFilterModeSelected(mode) {
  if (!pending_date_column.value)
    return;

  const column_config = pending_date_column.value;

  // Check if this field with this specific mode already exists
  if (widget_builder_config.value.quick_filters?.find?.(f => f.field === column_config.field && f.date_filter_mode === mode)) {
    pending_date_column.value = null;
    state.is_settings_open = false;
    return;
  }

  pending_date_column.value = null;
  state.is_settings_open = false;

  await addQuickFilter(column_config, mode);
}

async function addQuickFilter(column_config, date_filter_mode = null) {
  state.is_loading_values = true;

  try {
    const quick_filter_config = {
      field: column_config.field,
      alias: column_config.alias,
      label: column_config.label,
      type: column_config.field_type,
      table_name: column_config.table_name,
      name: column_config.field.split('→')[1] || column_config.field,
      stage_index: column_config.stage_index,
    };

    if (date_filter_mode) {
      quick_filter_config.date_filter_mode = date_filter_mode;
    }

    if (!widget_builder_config.value.quick_filters) {
      widget_builder_config.value.quick_filters = [];
    }
    widget_builder_config.value.quick_filters.push(quick_filter_config);

    await fetchDistinctValuesForFilter({ ...column_config, date_filter_mode });
  }
  catch (error) {
    logger.error('Error fetching distinct values:', error);
  }
  finally {
    state.is_loading_values = false;
  }
}

async function fetchDistinctValuesForFilter(filter_config) {
  try {
    let distinct_values;

    if (isDateField(filter_config.type || filter_config.field_type)) {
      // Use different options based on date_filter_mode
      if (filter_config.date_filter_mode === 'date_interval') {
        distinct_values = DATE_INTERVAL_OPTIONS.map(opt => opt.value);
      }
      else {
        distinct_values = DATE_FILTER_OPTIONS;
      }
    }
    else {
      distinct_values = await getDistinctValues(filter_config);
    }

    const filter_key = getFilterKey(filter_config);
    state.distinct_values_map[filter_key] = distinct_values;
  }
  catch (error) {
    logger.error('Error fetching distinct values for filter:', error);
    const filter_key = getFilterKey(filter_config);
    state.distinct_values_map[filter_key] = [];
  }
}

async function getDistinctValues(column_config) {
  try {
    const query_stages = props.config?.query?.stages;
    if (!query_stages?.length) {
      return [];
    }

    const payload = cloneDeep(query_stages);
    const stage_index = column_config.stage_index ?? payload.length - 1;
    const target_stage = payload[stage_index] || payload[payload.length - 1];

    target_stage.columns = [
      {
        alias: 'value',
        field: column_config.field,
      },
      {
        alias: 'count',
        field: column_config.field,
        agg: 'count',
      },
    ];
    target_stage.limit = 1000;
    target_stage.orderBy = [];
    target_stage.ignorePreviousStageColumns = true;

    const stage_config = getStageConfig(payload.slice(0, stage_index + 1));
    const response = await $services.bi_query.execute({
      id: bi_store.connection_id,
      body: stage_config,
    });

    const { data } = response.data;
    return data.map(item => item.value).filter(v => v !== null && v !== undefined && v !== '').sort();
  }
  catch (error) {
    logger.error('Error fetching column values from database:', error);
    const values_set = new Set();
    props.widgetData.forEach((row) => {
      const value = row[column_config.field];
      if (value !== null && value !== undefined && value !== '') {
        values_set.add(value);
      }
    });
    return Array.from(values_set).sort();
  }
}

function shouldShowAsTabs(filter_field) {
  const filter_key = getFilterKey(filter_field);
  const distinct_values = state.distinct_values_map[filter_key] || [];
  return distinct_values.length > 0 && distinct_values.length <= state.max_tab_values && displayed_quick_filters.value.length === 1;
}

function calculateMaxTabValues(filter_field) {
  if (!filters_container_ref.value)
    return 5;

  const filter_key = getFilterKey(filter_field);
  const distinct_values = state.distinct_values_map[filter_key] || [];

  if (distinct_values.length === 0)
    return 5;

  const container_width = filters_container_ref.value.clientWidth;

  const reserved_space = props.widgetId === 'preview' ? 40 : 20;
  const available_width = container_width - reserved_space;

  const tab_widths = distinct_values.map((value) => {
    const display_value = isDateIntervalFilter(filter_field)
      ? formatDateIntervalValue(value)
      : (isDateField(filter_field.type) ? formatDateFilterValue(value) : String(value));

    const text_width = getTextWidth(display_value, '500 12px Inter var');

    return text_width + 20 + 4;
  });

  let total_width = 0;
  let count = 0;

  for (const width of tab_widths) {
    if (total_width + width <= available_width) {
      total_width += width;
      count++;
    }
    else {
      break;
    }
  }

  return count > 0 ? count : 5;
}

function adjustMaxTabValues() {
  if (displayed_quick_filters.value.length === 1) {
    const filter_field = displayed_quick_filters.value[0];
    state.max_tab_values = calculateMaxTabValues(filter_field);
  }
}

function findMatchingGlobalFilter(filter_field) {
  if (props.widgetId === 'preview')
    return null;

  const global_filters = selected_dashboard_details.value?.meta?.global_filters || [];

  return global_filters.find((filter) => {
    const widget_mapping = filter.widgets[props.widgetId];
    if (isDateField(filter_field.type)) {
      // Match by alias for date_interval filters (as per applyGlobalFilters from BiGrid.vue)
      if (filter_field.date_filter_mode === 'date_interval') {
        return widget_mapping && widget_mapping.alias === filter_field.alias && filter.general.filter_type === filter_field.date_filter_mode;
      }
      return widget_mapping && widget_mapping.field === filter_field.field && filter.general.filter_type === filter_field.date_filter_mode;
    }
    return widget_mapping && widget_mapping.field === filter_field.field;
  });
}

function updateGlobalFilterIfMapped(filter_field, value_to_filter, remove_global_filter = false) {
  const matching_filter = findMatchingGlobalFilter(filter_field);

  if (!matching_filter)
    return false;

  if (!value_to_filter || remove_global_filter) {
    bi_store.removeAppliedGlobalFilter(matching_filter.uid);
    return true;
  }

  const is_date_field = isDateField(filter_field.type);
  const operator = is_date_field ? 'date_is' : 'in';

  if (get_applied_filters_for_dashboard.value[matching_filter.uid] && get_applied_filters_for_dashboard.value[matching_filter.uid].op === operator) {
    if (is_date_field) {
      bi_store.updateAppliedGlobalFilter(matching_filter.uid, {
        field: null,
        meta: {
          filter_config: {
            operator_value: value_to_filter,
          },
          is_stage_field: null,
        },
        op: operator,
        value: value_to_filter,
      });
    }
    else {
      bi_store.updateAppliedGlobalFilter(matching_filter.uid, {
        field: null,
        meta: {
          filter_config: {
            operator_value: [value_to_filter],
          },
          is_stage_field: null,
        },
        op: operator,
        value: [value_to_filter],
      });
    }
  }
  else {
    bi_store.updateAppliedGlobalFilter(matching_filter.uid, {
      field: null,
      meta: {
        filter_config: {
          operator_value: is_date_field ? value_to_filter : [value_to_filter],
        },
        is_stage_field: null,
      },
      op: operator,
      value: is_date_field ? value_to_filter : [value_to_filter],
    });
  }
  return true;
}

function onTabClick(filter_field, value) {
  const filter_key = getFilterKey(filter_field);
  let remove_global_filter = false;
  if (state.active_filters[filter_key] === value) {
    delete state.active_filters[filter_key];
    remove_global_filter = true;
  }
  else {
    state.active_filters[filter_key] = value;
  }
  const to_emit_apply_filters = !updateGlobalFilterIfMapped(filter_field, value, remove_global_filter);
  applyFilters(to_emit_apply_filters);
}

function onDropdownChange(filter_field, value) {
  const filter_key = getFilterKey(filter_field);
  let remove_global_filter = false;
  if (value) {
    if (state.active_filters[filter_key] === value) {
      delete state.active_filters[filter_key];
      remove_global_filter = true;
    }
    else {
      state.active_filters[filter_key] = value;
    }
  }
  else {
    delete state.active_filters[filter_key];
    remove_global_filter = true;
  }
  const to_emit_apply_filters = !updateGlobalFilterIfMapped(filter_field, value, remove_global_filter);
  applyFilters(to_emit_apply_filters);
}

function applyFilters(to_emit_apply_filters) {
  const quick_filters = props.config.quick_filters?.map?.((filter) => {
    const filter_key = getFilterKey(filter);
    return {
      ...filter,
      applied_value: state.active_filters[filter_key],
    };
  });
  bi_store.updateWidget(
    props.widgetId,
    {
      config: {
        ...props.config,
        quick_filters,
      },
    },
  );
  if (to_emit_apply_filters)
    emit('applyFilters', quick_filters);
}

function removeFilterField(filter_field) {
  if (props.widgetId === 'preview') {
    widget_builder_config.value.quick_filters = widget_builder_config.value.quick_filters?.filter?.((f) => {
      if (filter_field.date_filter_mode) {
        return !(f.field === filter_field.field && f.date_filter_mode === filter_field.date_filter_mode);
      }
      return f.field !== filter_field.field;
    });
  }
  const filter_key = getFilterKey(filter_field);
  delete state.active_filters[filter_key];
  delete state.search_queries[filter_key];
  applyFilters(true);
}

function getFilteredValues(filter_field) {
  const filter_key = getFilterKey(filter_field);
  const distinct_values = state.distinct_values_map[filter_key] || [];
  const search_query = state.search_queries[filter_key]?.toLowerCase() || '';
  if (!search_query) {
    return distinct_values;
  }

  return distinct_values.filter((value) => {
    const display_value = isDateIntervalFilter(filter_field)
      ? formatDateIntervalValue(value)
      : (isDateField(filter_field.type) ? formatDateFilterValue(value) : String(value));
    return display_value.toLowerCase().includes(search_query);
  });
}

async function loadDistinctValuesForAllFilters(force = false) {
  const filters = displayed_quick_filters.value || [];

  const promises = filters
    .filter((filter) => {
      if (force)
        return !isDateField(filter.type);
      const filter_key = getFilterKey(filter);
      return !state.distinct_values_map[filter_key];
    })
    .map(filter => fetchDistinctValuesForFilter(filter));

  await Promise.all(promises);

  let has_stale_filters = false;
  filters.forEach((filter) => {
    if (isDateField(filter.type))
      return;
    const filter_key = getFilterKey(filter);
    const active_value = state.active_filters[filter_key];
    if (active_value === undefined)
      return;
    const distinct_values = state.distinct_values_map[filter_key] || [];
    if (!distinct_values.includes(active_value)) {
      delete state.active_filters[filter_key];
      updateGlobalFilterIfMapped(filter, active_value, true);
      has_stale_filters = true;
    }
  });

  if (has_stale_filters) {
    applyFilters(true);
  }
}

watch(displayed_quick_filters, async (new_filters, old_filters) => {
  if (new_filters && new_filters.length > 0 && !isEqual(new_filters, old_filters)) {
    state.is_loading_values = true;
    await loadDistinctValuesForAllFilters();
    state.is_loading_values = false;
  }
}, { immediate: true, deep: true });

watch(() => props.config?.query?.stages, async (new_stages, old_stages) => {
  if (displayed_quick_filters.value?.length && !isEqual(new_stages, old_stages)) {
    state.is_loading_values = true;
    await loadDistinctValuesForAllFilters(true);
    state.is_loading_values = false;
  }
}, { deep: true });

watch(() => state.distinct_values_map, async () => {
  await nextTick();
  adjustMaxTabValues();
}, { deep: true });

onBeforeMount(() => {
  props.config.quick_filters?.forEach?.((filter) => {
    if (filter.applied_value) {
      const filter_key = getFilterKey(filter);
      state.active_filters[filter_key] = filter.applied_value;
    }
  });
});

onMounted(async () => {
  props.config.quick_filters?.forEach?.((filter) => {
    if (filter.applied_value) {
      updateGlobalFilterIfMapped(filter, filter.applied_value);
    }
  });

  await nextTick();
  if (filters_container_ref.value) {
    resize_observer = new ResizeObserver(() => {
      adjustMaxTabValues();
    });
    resize_observer.observe(filters_container_ref.value);
  }
});

onBeforeUnmount(() => {
  if (resize_observer) {
    resize_observer.disconnect();
    resize_observer = null;
  }
});
</script>

<template>
  <div class="flex items-start justify-between gap-3 min-h-[32px] pb-2 flex-shrink-0 bi-widget-quick-filters">
    <div ref="filters_container_ref" class="flex items-center gap-2 flex-1 flex-wrap">
      <template v-for="filter_field in displayed_quick_filters" :key="filter_field.field">
        <div v-if="shouldShowAsTabs(filter_field)" class="flex items-center gap-1">
          <HawkTabs
            :tabs="(state.distinct_values_map[getFilterKey(filter_field)] || []).map(value => ({
              uid: value,
              label: isDateIntervalFilter(filter_field) ? formatDateIntervalValue(value) : (isDateField(filter_field.type) ? formatDateFilterValue(value) : value),
              on_click: () => onTabClick(filter_field, value),
            }))"
            :active_item="state.active_filters[getFilterKey(filter_field)]"
            :show_item="Number.MAX_SAFE_INTEGER"
            size="xs"
            :space="1"
            :class="props.config.chart.type === 'number_chart' ? 'bg-white p-0.5 rounded' : ''"
          />
          <div
            v-if="props.widgetId === 'preview'"
            v-tippy="{ content: $t('Remove'), placement: 'top' }"
            class="ml-1 text-gray-400 hover:text-gray-600 cursor-pointer"
            @click="removeFilterField(filter_field)"
          >
            <IconHawkXClose class="w-3 h-3" />
          </div>
        </div>

        <div v-else class="flex items-center gap-1">
          <HawkMenu :teleport="props.widgetId === 'preview' ? false : true" position="bottom-right" additional_trigger_classes="!ring-0" additional_dropdown_classes="w-[210px]">
            <template #trigger="{ open }">
              <div class="flex items-center justify-between px-2.5 py-1 gap-2 border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50">
                <div class="flex items-center">
                  <span class="text-xs font-medium bi-exportable-text">
                    {{ filter_field.label }}
                    <span v-if="state.active_filters[getFilterKey(filter_field)]" class="text-primary-600 font-semibold">: {{ isDateIntervalFilter(filter_field) ? formatDateIntervalValue(state.active_filters[getFilterKey(filter_field)]) : (isDateField(filter_field.type) ? formatDateFilterValue(state.active_filters[getFilterKey(filter_field)]) : state.active_filters[getFilterKey(filter_field)]) }}</span>
                  </span>
                </div>
                <div>
                  <IconHawkChevronUp v-if="open" class="w-3 h-3" />
                  <IconHawkChevronDown v-else class="w-3 h-3" />
                </div>
              </div>
            </template>
            <template #content="{ close }">
              <div class="p-1 min-w-[200px]">
                <div
                  v-if="state.active_filters[getFilterKey(filter_field)]"
                  class="px-3 py-2 text-sm cursor-pointer rounded hover:bg-gray-50 text-gray-500 mb-1"
                  @click="onDropdownChange(filter_field, null); close()"
                >
                  {{ $t('Clear') }}
                </div>
                <hr v-if="state.active_filters[getFilterKey(filter_field)]" class="mb-1">
                <div v-if="(state.distinct_values_map[getFilterKey(filter_field)] || []).length > 5" class="mb-1">
                  <HawkSearchInput
                    v-model="state.search_queries[getFilterKey(filter_field)]"
                    :placeholder="$t('Search')"
                    :debounce_time="0"
                    full_width
                    :additional-classes="{
                      ElementLayout: {
                        innerWrapper: 'border-x-0 border-b',
                      },
                      TextElement: {
                        inputContainer: 'border-none',
                      },
                    }"
                    :remove-classes="{
                      TextElement: {
                        inputContainer_default: 'focused:form-ring',
                        inputContainer_success: 'focused:form-ring',
                      },
                    }"
                    :icon-class="['!h-4', '!w-4']"
                    @keydown.space.stop
                  />
                </div>
                <div class="max-h-60 overflow-y-auto scrollbar">
                  <div
                    v-for="(value, idx) in getFilteredValues(filter_field)"
                    :key="value"
                    class="px-3 py-2 text-sm cursor-pointer rounded hover:bg-gray-50"
                    :class="[
                      state.active_filters[getFilterKey(filter_field)] === value ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-700',
                      idx === getFilteredValues(filter_field).length - 1 ? '' : 'mb-1',
                    ]"
                    @click="onDropdownChange(filter_field, value); close()"
                  >
                    {{ isDateIntervalFilter(filter_field) ? formatDateIntervalValue(value) : (isDateField(filter_field.type) ? formatDateFilterValue(value) : value) }}
                  </div>
                  <div v-if="getFilteredValues(filter_field).length === 0" class="px-3 py-2 text-sm text-gray-500 text-center">
                    {{ $t('No results found') }}
                  </div>
                </div>
              </div>
            </template>
          </HawkMenu>
          <div
            v-if="props.widgetId === 'preview'"
            v-tippy="{ content: $t('Remove'), placement: 'top' }"
            class="text-gray-400 hover:text-gray-600 cursor-pointer"
            @click="removeFilterField(filter_field)"
          >
            <IconHawkXClose class="w-3 h-3" />
          </div>
        </div>
      </template>
      <HawkLoader v-if="state.is_loading_values" container_class="ml-1 bi-non-exportable" :height="4" :width="4" />
    </div>

    <div v-if="widgetId === 'preview'" class="mr-2">
      <HawkMenu v-model="state.is_settings_open" position="fixed" additional_trigger_classes="!ring-0">
        <template #trigger>
          <div
            v-tippy="{ content: $t('Add quick filter'), placement: 'top' }"
            class="p-1.5 text-gray-600 hover:text-gray-900 rounded hover:bg-gray-100 transition-colors"
          >
            <IconHawkFilterFunnelOne class="w-4 h-4" />
          </div>
        </template>
        <template #content="{ close }">
          <div v-if="pending_date_column" class="p-1 min-w-[200px] bg-white rounded-lg">
            <div
              class="px-3 py-2 text-sm cursor-pointer rounded hover:bg-gray-50 text-gray-500"
              @click="pending_date_column = null"
            >
              {{ $t('Back') }}
            </div>
            <hr class="my-1">
            <div class="px-3 py-2 text-xs font-semibold text-gray-500">
              {{ $t('Filter type') }}
            </div>
            <div
              class="px-3 py-2 text-sm cursor-pointer rounded hover:bg-gray-50 text-gray-700 flex items-center gap-2"
              @click="onDateFilterModeSelected('date_range'); close()"
            >
              {{ $t('Date range') }}
            </div>
            <div
              class="px-3 py-2 text-sm cursor-pointer rounded hover:bg-gray-50 text-gray-700 flex items-center gap-2"
              @click="onDateFilterModeSelected('date_interval'); close()"
            >
              {{ $t('Date interval') }}
            </div>
          </div>
          <BiQueryBuilderColumnPicker
            v-else
            :stages="stages"
            :stage-index="0"
            :has-functions="false"
            :show-stage-header="true"
            @selected="onColumnSelected"
          />
        </template>
      </HawkMenu>
    </div>
  </div>
</template>
